import {ChangeDetectionStrategy, Component, Inject, Injectable, signal} from "@angular/core";
import {ArkivnodeId, ImportresultatModell, ImportValideringsfeil} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {Observable, Subscription} from "rxjs";
import {MAT_DIALOG_DATA, MatDialog, MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle} from "@angular/material/dialog";
import {HttpEvent, HttpEventType, HttpProgressEvent, HttpResponse} from "@angular/common/http";
import {MatIcon} from "@angular/material/icon";
import {MatButton} from "@angular/material/button";
import {TranslateModule} from "@ngx-translate/core";
import {FormsModule} from "@angular/forms";
import {TypetValideringsresultatModell} from "felles-ng2/tjenester/ressurser/ng2HttpKlientTilleggstyper";
import {FormaterFilstørrelsePipe} from "felles-ng2/pipes/FormaterFilstørrelsePipe";
import {MatProgressSpinner} from "@angular/material/progress-spinner";

import {ArkivDatatjeneste, ArkivMappenodeDLM} from "felles-ng2/tjenester/datalager/ArkivDatatjeneste";
import {erValideringsfeilException} from "felles-ng2/infrastruktur/http/httpErrorResponseTyper";
import {okTypetValideringsresultatModell} from "felles-ng2/støttefunksjoner/valideringsresultatModellHjelpere";
import {erIkkeNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {Feilhåndteringsstrategi} from "felles-ng2/tjenester/datalager/datatjeneste";

const debug = initDebugger('ArkivImporterZipDialogKomponent');

/**
 * Dialog for import til arkiv fra zip
 */
@Component({
    selector: 'arkiv-importer-zip-dialog',
    templateUrl: 'arkiv-importer-zip-dialog.html',
    styleUrl: 'arkiv-importer-zip-dialog.scss',
    imports: [
        MatIcon,
        TranslateModule,
        FormsModule,
        MatButton,
        MatDialogActions,
        MatDialogContent,
        FormaterFilstørrelsePipe,
        MatProgressSpinner,
        MatDialogTitle
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ArkivImporterZipDialogKomponent {

    static vis(dialog: MatDialog, forelderMappe: ArkivMappenodeDLM, zipFil: File) {
        return dialog.open<ArkivImporterZipDialogKomponent, DialogParametre, boolean>(ArkivImporterZipDialogKomponent,
          {
              data: {mappe: forelderMappe, zipFil},
              panelClass: 'arkiv-importer-zip-dialog',
              width: '600px',
              minHeight: '300px'
          });
    }

    /** Mappen innholdet i zip-fil skal importeres til */
    mappe: ArkivMappenodeDLM; //fra DialogParametre
    zipFil: File; //fra DialogParametre

    readonly maksFilstoerrelse = 1024 * 1024 * 100;
    forStor = false;
    modus = signal<'importer' | 'importerer' | 'importert' | 'feil'>('importer');
    importresultat = signal<ImportresultatModell | null>(null);
    progresjon = signal(1);
    valideringsfeil: TypetValideringsresultatModell<ImportValideringsfeil> = okTypetValideringsresultatModell();
    muligFeilkoder = ImportValideringsfeil;

    private opplasting: Subscription | null = null;

    constructor(@Inject(MAT_DIALOG_DATA) parametre: DialogParametre, public dialogRef: MatDialogRef<ArkivImporterZipDialogKomponent>, private avhengigheter: ArkivImporterZipDialogKomponentAvhengigheter) {
        this.mappe = parametre.mappe;
        this.zipFil = parametre.zipFil;

        this.forStor = this.zipFil.size > this.maksFilstoerrelse;
    }

    startImport() {
        this.modus.set('importerer');
        this.opplasting = this.avhengigheter.importer(this.mappe.id, this.zipFil)
          .subscribe({
              next: event => {
                  if (event.type == HttpEventType.UploadProgress) {
                      const progresjon = event as HttpProgressEvent;
                      this.progresjon.set(Math.floor(100.0 * progresjon.loaded / this.zipFil.size))
                  }
                  if (event.type == HttpEventType.Response) {
                      const respons = event as HttpResponse<ImportresultatModell>;
                      this.importFerdig(respons.body!);
                  }
              },
              error: feil => this.håndterRequestFeil(feil)
          });
    }

    lukk() {
        this.dialogRef.close(true);
    }

    avbryt() {
        this.opplasting?.unsubscribe();
        //signaliserer til kaller at import har (potensielt) skjedd såfremt import har blitt initiert (this.opplasting != null)
        this.dialogRef.close(erIkkeNil(this.opplasting));
    }

    //private

    private importFerdig(importresultat: ImportresultatModell) {
        debug('arkivImportert', importresultat);
        this.modus.set('importert');
        this.importresultat.set(importresultat);
    }

    private håndterRequestFeil(e: any) {
        this.modus.set('feil');
        if (erValideringsfeilException<ImportValideringsfeil>(e))
            this.valideringsfeil = e.error;
    }
}

export interface DialogParametre {
    mappe: ArkivMappenodeDLM;
    zipFil: File;
}

@Injectable()
export class ArkivImporterZipDialogKomponentAvhengigheter {

    constructor(private arkivDatatjeneste: ArkivDatatjeneste) {
    }

    importer(mappeId: ArkivnodeId, zipFil: File): Observable<HttpEvent<ImportresultatModell>> {
        return this.arkivDatatjeneste.importer(mappeId, zipFil, Feilhåndteringsstrategi.propagerSomError);
    }
}
